import React, { useCallback, useState } from 'react';
import { Segmented } from 'antd';

export type Density = 'comfortable' | 'compact';

const storageKey = (tableId: string) => `table-density:${tableId}`;

/** Row density for one table, remembered per browser. */
export function useDensity(tableId: string): [Density, (d: Density) => void] {
  const [density, setDensityState] = useState<Density>(() => {
    try {
      return localStorage.getItem(storageKey(tableId)) === 'compact' ? 'compact' : 'comfortable';
    } catch {
      return 'comfortable';
    }
  });

  const setDensity = useCallback(
    (d: Density) => {
      setDensityState(d);
      try {
        localStorage.setItem(storageKey(tableId), d);
      } catch {
        // quota or private mode — the choice just won't survive the reload
      }
    },
    [tableId],
  );

  return [density, setDensity];
}

export interface DensityToggleProps {
  value: Density;
  onChange: (d: Density) => void;
  size?: 'small' | 'middle' | 'large';
}

const DensityToggle: React.FC<DensityToggleProps> = ({ value, onChange, size = 'small' }) => (
  <Segmented
    size={size}
    value={value}
    onChange={(v) => onChange(v as Density)}
    options={[
      { label: 'Comfortable', value: 'comfortable' },
      { label: 'Compact', value: 'compact' },
    ]}
  />
);

export default DensityToggle;
